import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { DateTime } from 'luxon';
import { Subscription } from 'rxjs';
import { DateFormatService } from 'shared/services/date-format.service';

/**
 * Komponent do formatowania dat z ISO string na czytelny format
 * Używa Luxon do parsowania i formatowania
 * Format domyślny pobierany jest z ustawień użytkownika (DateFormatService)
 * 
 * Przykład użycia:
 * <date-formatter [date]="'2026-01-30'"></date-formatter>
 * <date-formatter [date]="'2026-01-30T14:30:00Z'" [showTime]="true"></date-formatter>
 * <date-formatter [date]="someDate" [format]="'dd/MM/yyyy'"></date-formatter>
 */
@Component({
  standalone: true,
  selector: 'date-formatter',
  imports: [CommonModule],
  templateUrl: './date-formatter.component.html',
  styleUrls: ['./date-formatter.component.scss']
})
export class DateFormatterComponent implements OnChanges, OnInit, OnDestroy {

  /** Data do sformatowania (ISO string, DateTime lub Date) */
  @Input() date: string | DateTime | Date | null | undefined;
  
  /** Format daty (jeśli null, używa globalnego z ustawień użytkownika) */
  @Input() format: string | null = null;
  
  /** Czy pokazywać czas (dodaje HH:mm do formatu) */
  @Input() showTime: boolean = false;
  
  /** Czy pokazywać sekundy (tylko gdy showTime=true) */
  @Input() showSeconds: boolean = false;
  
  /** Tekst gdy data jest pusta/null */
  @Input() emptyText: string = '—';
  
  output: string = '';

  private _subscription: Subscription | null = null;
  private _globalFormat: string = 'dd.MM.yyyy';

  constructor(private _dateFormatService: DateFormatService) {}

  ngOnInit(): void {
    this._subscription = this._dateFormatService.format$.subscribe(format => {
      this._globalFormat = format;
      this.buildFormat();
    });
  }

  ngOnDestroy(): void {
    this._subscription?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.buildFormat();
  }
  
  buildFormat(): void {
    if (!this.date) {
      this.output = this.emptyText;
      return;
    }

    try {
      let dt: DateTime;

      if (this.date instanceof DateTime) {
        dt = this.date;
      } else if (this.date instanceof Date) {
        dt = DateTime.fromJSDate(this.date);
      } else if (typeof this.date === 'string') {
        // Próbuj różne formaty ISO
        dt = DateTime.fromISO(this.date);
        
        // Jeśli nie udało się, spróbuj jako SQL date
        if (!dt.isValid) {
          dt = DateTime.fromSQL(this.date);
        }
        
        // Jeśli nadal nie udało się, spróbuj jako format YYYY-MM-DD
        if (!dt.isValid && /^\d{4}-\d{2}-\d{2}$/.test(this.date)) {
          dt = DateTime.fromFormat(this.date, 'yyyy-MM-dd');
        }
      } else {
        this.output = this.emptyText;
        return;
      }

      if (!dt.isValid) {
        console.warn('DateFormatterComponent: Invalid date', this.date);
        this.output = this.emptyText;
        return;
      }

      // Zbuduj format - użyj jawnie podanego lub globalnego z ustawień użytkownika
      let finalFormat = this.format || this._globalFormat;
      if (this.showTime) {
        finalFormat += this.showSeconds ? ' HH:mm:ss' : ' HH:mm';
      }

      this.output = dt.setLocale('pl').toFormat(finalFormat);
    } catch (error) {
      console.error('DateFormatterComponent: Error formatting date', error);
      this.output = this.emptyText;
    }
  }
}
